import cmsParser from 'Common/util/cmsParser';
import type { PageEntry, Node, Entry } from 'contensis-delivery-api';
import type { PagedList } from 'contensis-core-api';
import type { Props } from './Listing';
import { colours } from './Listing.styled';

import { Col } from 'Components/Grid';

import SolentStory, { SolentStoryCardProps } from 'Components/Card/SolentStory';
import { ActiveFilter } from 'Modules/Filters/SolentStories';

export type PageData = {
    entry: PageEntry;
    ancestors: Node[];
    losStories: Node[];
    stories: PagedList<Entry>;
    featuredStories: PagedList<Entry>;
    subjectsTaxonomy: Entry;
    levelOfStudyTaxonomy: Entry;
    activeFilterQueries: Record<FilterTypes, QueryFilter[] | null>;
};

type FilterTypes = 'subject' | 'levelOfStudy';
export type QueryFilter = { id: string };

const datamap = (data: PageData): Props => {
    const {
        entry,
        stories,
        ancestors,
        subjectsTaxonomy,
        featuredStories,
        levelOfStudyTaxonomy,
        losStories,
        activeFilterQueries
    } = data;

    const { title, sys, heroBannerWaveDefault } = entry;

    // Remove filter taxonomies that have no stories attributed [start]
    const losArray: string[] = [];

    losStories
        .filter(story => story.levelOfStudy.length >= 1)
        .map(item =>
            item.levelOfStudy
                .map(internalItem => internalItem.name)
                .map(item => losArray.push(item))
        );

    const deDupedLosArray = new Set(losArray);

    const levelOfStudyFilterArray = levelOfStudyTaxonomy.children
        .filter(item => deDupedLosArray.has(item.name))
        .map(item => ({
            name: item.name,
            id: item.key
        }));

    // Remove filter taxonomies that have no stories attributed [end]
    const subjectFilterArray = subjectsTaxonomy.children.map(item => ({
        name: item.name,
        id: item.key
    }));

    const FeaturedStory: SolentStoryCardProps = {
        featured: true,
        heading: featuredStories.items[0].entryTitle,
        headingType: 'h3',
        title: featuredStories.items[0].student.currentRole,
        organisation: featuredStories.items[0].student.company,
        minutes: featuredStories.items[0].estimatedMinutesRead,
        quote: featuredStories.items[0].courseCardQuote,
        link: {
            href: featuredStories.items[0].sys.uri,
            ariaLabel: `${featuredStories.items[0].entryTitle} graduate story`
        },
        themeColor: colours[0],
        images: {
            avatar: [
                cmsParser.image(
                    featuredStories.items[0].listingCardImageDefault
                        ? featuredStories.items[0].listingCardImageDefault
                        : featuredStories.items[0].listingCardImage
                ),

                cmsParser.image(
                    featuredStories.items[0].listingCardImageOnHover
                        ? featuredStories.items[0].listingCardImageOnHover
                        : featuredStories.items[0].listingCardImageDefault
                        ? featuredStories.items[0].listingCardImageDefault
                        : featuredStories.items[0].listingCardImage
                )
            ],
            featureImage: cmsParser.image(featuredStories.items[0].cardImage)
        }
    };

    return {
        breadcrumbs: cmsParser.breadcrumbs([...ancestors, { entry: { title: title }, id: sys.id }]),
        heroBanner: {
            ...cmsParser.heroBannerWave(heroBannerWaveDefault),
            innerColWidth: 5,
            children: (
                <Col m={[6, 13]}>
                    <br />
                    <SolentStory {...FeaturedStory} />
                </Col>
            )
        },
        filters: {
            id: 'storiesListing',
            numberOfResults: stories.totalCount,
            initialActiveFilters: Object.keys(activeFilterQueries)
                .map((key: FilterTypes) => {
                    const activeFilters = activeFilterQueries[key];

                    if (activeFilters) {
                        return activeFilters.map(activeFilter => ({
                            id: activeFilter as unknown as string,
                            type: key,
                            label: [...subjectFilterArray, ...levelOfStudyFilterArray].find(
                                filter => filter.id === activeFilter
                            ).name
                        })) as ActiveFilter[];
                    }
                    return [];
                })
                .flat(),
            filters: [
                {
                    type: 'subject',
                    label: 'Subject',
                    options: subjectFilterArray.map(item => ({
                        id: item.id,
                        name: item.name,
                        label: item.name,
                        value: item.id
                    }))
                },
                {
                    type: 'levelofstudy',
                    label: 'Study level',
                    options: levelOfStudyFilterArray.map(item => ({
                        id: item.id,
                        name: item.name,
                        label: item.name,
                        value: item.id
                    }))
                }
            ]
        },
        results: stories.items.map((item, index) => ({
            id: item.sys.id,
            featured: false,
            videoStory: false,
            heading: item.entryTitle,
            subject: item.subject.name,
            title: item.student.currentRole,
            organisation: item.student.company,
            minutes: item.estimatedMinutesRead ? item.estimatedMinutesRead : 5,
            quote: item.courseCardQuote,
            link: {
                href: item.sys.uri,
                ariaLabel: `${item.entryTitle} graduate story`
            },
            themeColor: colours[index % colours.length],
            images: {
                avatar: [
                    cmsParser.image(
                        item.listingCardImageDefault
                            ? item.listingCardImageDefault
                            : item.listingCardImage
                    ),

                    cmsParser.image(
                        item.listingCardImageOnHover
                            ? item.listingCardImageOnHover
                            : item.listingCardImageDefault
                            ? item.listingCardImageDefault
                            : item.listingCardImage
                    )
                ],
                featureImage: cmsParser.image(item.cardImage)
            }
        })),
        pagination: {
            id: 'graduate-stories-listing-pagination',
            totalCount: stories.totalCount,
            pageSize: stories.pageSize,
            initialPageIndex: stories.pageIndex
        }
    };
};

export default datamap;
